import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import FadeInOnScroll from '../components/shared/FadeInOnScroll';
import { GalleryEvent, GalleryModal, GallerySectionWithParallax } from '../components/shared/GalleryComponents';
import { galleriesAPI, siteSettingsAPI } from '../services/api';
import './Studios.css';

import Alys_80b from '../assets/images/sides/Alys_80b.png';
import Bianca97 from '../assets/images/sides/Bianca97.png';
import Bianca111b from '../assets/images/sides/Bianca111b.png';
import Kris_36c from '../assets/images/sides/Kris_36c.png';
import Kris_33a from '../assets/images/sides/Kris_33a.png';
import Rico_1b from '../assets/images/sides/Rico_1b.png';

const serviceCards = [
  {
    icon: 'calendar',
    title: 'Event Coverage',
    description: 'Concerts, parties, conventions, private events, and celebrations.'
  },
  {
    icon: 'brand',
    title: 'Business & Brand Shoots',
    description: 'Professional branding, marketing, and corporate photography.'
  },
  {
    icon: 'design',
    title: 'Graphic Design',
    description: 'Custom logos, flyers, posters, and other visual design work.'
  },
  {
    icon: 'plus',
    title: 'Special Occasions',
    description: 'Weddings, anniversaries, engagements, and milestones.'
  },
  {
    icon: 'camera',
    title: 'Commissions',
    description: 'Custom photoshoots tailored to your own concept or vision.'
  },
  {
    icon: 'pin',
    title: 'Studio & Location Shoots',
    description: 'In-house studio sessions and flexible location shoots upon request.'
  }
];

const defaultGalleryEvents = [
  {
    id: 1,
    title: 'Portrait Collection',
    thumbnail: Alys_80b,
    images: [Alys_80b, Bianca97, Bianca111b]
  },
  {
    id: 2,
    title: 'Fashion Photography',
    thumbnail: Bianca97,
    images: [Bianca97, Kris_36c, Kris_33a, Rico_1b, Alys_80b, Bianca111b]
  },
  {
    id: 3,
    title: 'Creative Session',
    thumbnail: Bianca111b,
    images: [Bianca111b, Alys_80b, Kris_36c]
  }
];

const StudioIcon = ({ type }) => {
  switch (type) {
    case 'calendar':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      );
    case 'brand':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 21V9l8-5 8 5v12" />
          <path d="M9 21V12h6v9" />
          <path d="M2 21h20" />
        </svg>
      );
    case 'person':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-5 3-8 8-8s8 3 8 8" />
        </svg>
      );
    case 'design':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 20h16" />
          <path d="M7 16l9.5-9.5a2.1 2.1 0 0 1 3 3L10 19l-4 1 1-4z" />
          <path d="M14.5 8.5l3 3" />
        </svg>
      );
    case 'plus':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      );
    case 'camera':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 8h4l2-3h4l2 3h4v11H4z" />
          <circle cx="12" cy="14" r="4" />
        </svg>
      );
    case 'pin':
    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 21s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      );
  }
};

const Studios = () => {
  const { galleryId } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [studiosHeroBg, setStudiosHeroBg] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [galleryEvents, setGalleryEvents] = useState(defaultGalleryEvents);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await siteSettingsAPI.get('studios_hero_bg_url');

        if (response.success && response.data) {
          setStudiosHeroBg(response.data.studios_hero_bg_url || '');
        }
      } catch (error) {
        console.error('Error loading Studios settings:', error);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    const loadGalleries = async () => {
      try {
        const response = await galleriesAPI.getAll('veloura');

        if (response.success && response.data && response.data.length > 0) {
          const transformed = response.data.map((gallery) => ({
            id: gallery.id,
            title: gallery.title,
            thumbnail: gallery.thumbnail,
            images: (gallery.images || []).map((img) => typeof img === 'string' ? img : img.url)
          }));

          setGalleryEvents(transformed);
        } else {
          setGalleryEvents(defaultGalleryEvents);
        }
      } catch (error) {
        console.error('Error loading galleries:', error);
        setGalleryEvents(defaultGalleryEvents);
      }
    };

    loadGalleries();
  }, []);

  useEffect(() => {
    if (!galleryId || galleryEvents.length === 0) return;

    const linkedGallery = galleryEvents.find((event) => String(event.id) === String(galleryId));
    if (linkedGallery) {
      setSelectedEvent(linkedGallery);
      setIsModalOpen(true);
    }
  }, [galleryId, galleryEvents]);

  const openGallery = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
    window.history.replaceState(null, '', `/studios/${event.id}`);
  };

  const closeGallery = () => {
    setIsModalOpen(false);
    window.history.replaceState(null, '', '/studios');
    setTimeout(() => setSelectedEvent(null), 300);
  };

  return (
    <div className="studios-page-wrapper">
      <ResponsiveNavbar
        menuItems={[
          { type: 'link', label: 'Veloura', to: '/' },
          { type: 'link', label: 'Shop', to: '/shop' },
          { type: 'link', label: 'Team', to: '/team' },
          { type: 'link', label: 'Careers', to: '/careers' },
          { type: 'link', label: 'Partners', to: '/partners' },
          { type: 'link', label: 'News', to: '/news' },
          { type: 'link', label: 'Account', to: '/account' }
        ]}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      <main className="studios-page">
        <section
          className="studios-hero"
          style={studiosHeroBg ? { '--studios-hero-image': `url(${studiosHeroBg})` } : undefined}
        >
          <div className="studios-hero-overlay" />

          <div className="studios-hero-content">
            <h1 className="studios-hero-logo-title">
              <img
                src="https://i.ibb.co/67K1dhdZ/Veloura-Studios-Logo-Cropped.png"
                alt="Veloura Studios"
                className="studios-hero-logo"
              />
            </h1>
            <p>
              Our professional photography branch, and where Veloura originally began. Our photographers are dedicated
              to creating, capturing and presenting both people and companies alike, focusing on detail-driven, high
              quality photography for a reasonable price, with room to expand further for those looking to achieve more.
            </p>
          </div>
        </section>

        <section className="studios-intro-section">
          <div className="studios-intro-card">
            <span className="studios-section-label">About the Studio</span>
            <h2>Creative work, captured with intent.</h2>
            <p>
              Veloura Studios is our professional photography branch, and where Veloura originally began. Over time,
              it has grown into a wider creative brand that now goes beyond photography, while still keeping it at its core.
            </p>
            <p>
              Our photographers are dedicated to creating, capturing, and presenting both people and companies alike.
              We focus on high quality work at a reasonable price, with the option to expand further for those looking
              to achieve more.
            </p>
            <p>
              Our team is flexible and able to support both in-house studio sessions and location shoots upon request,
              giving every project the space, setting, and direction it needs.
            </p>
          </div>
        </section>

        <section className="studios-services-section" aria-labelledby="studios-services-title">
          <div className="studios-section-heading">
            <span className="studios-section-label">Services</span>
            <h2 id="studios-services-title">What We Offer</h2>
            <p>
              From personal shoots to full event coverage, Veloura Studios can be shaped around your idea.
            </p>
          </div>

          <div className="studios-services-frame">
            <div className="studios-services-grid">
              {serviceCards.map((service) => (
                <article className="studios-service-card" key={service.title}>
                  <div className="studios-service-icon">
                    <StudioIcon type={service.icon} />
                  </div>

                  <div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="studios-cta-strip">
            <p>
              We’re also always open to supporting other creative projects. If you have an idea in mind, open a
              Contact Us ticket on our portal and tell us what you want to create.
            </p>

            <a className="studios-primary-button" href="https://gtaw.link/veloura">
              Book Us Today
            </a>
          </div>
        </section>

        <GallerySectionWithParallax>
          <FadeInOnScroll>
            <h2 className="section-title">Gallery</h2>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.2}>
            <p className="section-intro">
              A glimpse into our world of creativity and artistry.
            </p>
          </FadeInOnScroll>

          <div className="gallery-grid">
            {galleryEvents.map((event, index) => (
              <GalleryEvent
                key={event.id}
                event={event}
                index={index}
                delay={index * 0.1}
                onOpen={openGallery}
              />
            ))}
          </div>
        </GallerySectionWithParallax>

        <AnimatePresence mode="wait">
          {isModalOpen && selectedEvent && (
            <GalleryModal
              key={selectedEvent.id}
              event={selectedEvent}
              isOpen={isModalOpen}
              onClose={closeGallery}
            />
          )}
        </AnimatePresence>
      </main>

      <Footer />
    </div>
  );
};

export default Studios;
